import type {
  ListNotificationsResponse,
  NotificationPreferencesResponse,
  NotificationType,
  UpdateNotificationPreferenceRequest,
  WebPushSubscriptionInput,
  WebPushSubscriptionResponse,
  WebPushSubscriptionsResponse,
} from '@simple-agent-manager/shared';

import { API_URL, request } from './client';

export async function listNotifications(opts?: {
  cursor?: string;
  limit?: number;
  filter?: 'all' | 'unread';
  type?: NotificationType;
  projectId?: string;
  sessionId?: string;
}): Promise<ListNotificationsResponse> {
  const params = new URLSearchParams();
  if (opts?.cursor) params.set('cursor', opts.cursor);
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.filter) params.set('filter', opts.filter);
  if (opts?.type) params.set('type', opts.type);
  if (opts?.projectId) params.set('projectId', opts.projectId);
  if (opts?.sessionId) params.set('sessionId', opts.sessionId);
  const qs = params.toString();
  return request<ListNotificationsResponse>(`/api/notifications${qs ? `?${qs}` : ''}`);
}

export async function getNotificationUnreadCount(): Promise<{ count: number }> {
  return request<{ count: number }>('/api/notifications/unread-count');
}

export async function markNotificationRead(id: string): Promise<void> {
  await request('/api/notifications/' + encodeURIComponent(id) + '/read', { method: 'POST' });
}

export async function markAllNotificationsRead(): Promise<void> {
  await request('/api/notifications/read-all', { method: 'POST' });
}

export async function dismissNotification(id: string): Promise<void> {
  await request('/api/notifications/' + encodeURIComponent(id) + '/dismiss', { method: 'POST' });
}

export async function getNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  return request<NotificationPreferencesResponse>('/api/notifications/preferences');
}

export async function updateNotificationPreference(
  pref: UpdateNotificationPreferenceRequest
): Promise<void> {
  await request('/api/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(pref),
  });
}

export async function getVapidPublicKey(): Promise<{ publicKey: string | null }> {
  return request<{ publicKey: string | null }>('/api/config/vapid-public-key');
}

export async function subscribeWebPush(
  subscription: WebPushSubscriptionInput
): Promise<WebPushSubscriptionResponse> {
  return request<WebPushSubscriptionResponse>('/api/notifications/push/subscriptions', {
    method: 'POST',
    body: JSON.stringify(subscription),
  });
}

export async function unsubscribeWebPush(endpoint: string): Promise<void> {
  await request('/api/notifications/push/subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
}

export async function listWebPushSubscriptions(): Promise<WebPushSubscriptionsResponse> {
  return request<WebPushSubscriptionsResponse>('/api/notifications/push/subscriptions');
}

/**
 * Build the WebSocket URL for real-time notification delivery.
 * Auth cookie is sent automatically via the WebSocket connection.
 */
export function getNotificationWsUrl(): string {
  const base = API_URL.replace(/^http/, 'ws');
  return `${base}/api/notifications/ws`;
}

export interface AttentionRequestContext {
  request: {
    id: string;
    reason: string | null;
    options: string[];
    createdAt: number;
    resolvedAt: number | null;
    resolvedAnswer: string | null;
    expired: boolean;
    canAnswer: boolean;
  };
  session: { id: string; topic: string | null };
  messages: (import('./sessions').ChatMessageResponse & { truncated?: boolean })[];
}

/** Full saved question independent of whichever chat page is currently loaded. */
export async function getAttentionRequestContext(
  projectId: string,
  sessionId: string,
  markerId: string,
  context: boolean,
  signal?: AbortSignal
): Promise<AttentionRequestContext> {
  return request<AttentionRequestContext>(
    `/api/projects/${encodeURIComponent(projectId)}/sessions/${encodeURIComponent(sessionId)}/attention/${encodeURIComponent(markerId)}${context ? '?context=true' : ''}`,
    signal ? { signal } : {}
  );
}
